import { useCallback, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type ProjectDetail } from '@/entities/project';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useUpdatePublishedProject } from '@/features/edit-project/api/useUpdatePublishedProject.mutation';
import {
  buildCreateProjectSchema,
  type CreateProjectFormData,
} from '@/features/edit-project/model/schema';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { mapFormDataToUpdatePayload, mapProjectToFormData } from './EditProjectModal.utils';

export interface UseEditProjectFormOptions {
  project: ProjectDetail;
  onSuccess?: () => void;
  onClose?: () => void;
}

export function useEditProjectForm(options: UseEditProjectFormOptions) {
  const { project, onSuccess, onClose } = options;
  const { t } = useTranslation('projects');
  const { t: tValidation } = useTranslation('validation');
  const workspaceId = useCurrentWorkspaceId();
  const updateProjectMutation = useUpdatePublishedProject({ workspaceId });

  const defaultValues = useMemo(() => mapProjectToFormData(project), [project]);

  const formSchema = useMemo(() => buildCreateProjectSchema(tValidation), [tValidation]);

  const form = useForm<CreateProjectFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onTouched',
  });

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  // `onClose` alone — the host unmounts on its own timer. See `useModalState`.
  const requestClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const confirmIfDirty = useCallback(
    (action: () => void) => {
      if (form.formState.isDirty) {
        pendingActionRef.current = action;
        setShowUnsavedModal(true);
        return;
      }
      action();
    },
    [form.formState.isDirty],
  );

  const handleConfirmLeave = useCallback(() => {
    setShowUnsavedModal(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, []);

  const handleCancelLeave = useCallback(() => {
    setShowUnsavedModal(false);
    pendingActionRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    confirmIfDirty(requestClose);
  }, [confirmIfDirty, requestClose]);

  const onSubmit = useCallback(
    async (formData: CreateProjectFormData) => {
      try {
        const payload = mapFormDataToUpdatePayload(formData);
        // useUpdatePublishedProject handles the cache invalidations (project detail,
        // workspace sidebar prefix, filter-options). The hook accepts
        // workspaceId at construction so the workspace-scoped invalidations
        // happen automatically without leaking that wiring into this caller.
        await updateProjectMutation.mutateAsync({ projectId: project.platformProjectId, payload });

        notificationService.showSuccess(
          t('editProject.useEditProjectForm.projectUpdatedSuccessfully'),
          {
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'bottom',
            },
          },
        );
        onSuccess?.();
        requestClose();
      } catch (error) {
        const { status, detail } = parseProblemDetail(
          error,
          t('editProject.useEditProjectForm.somethingWentWrongWhileUpdating'),
        );
        if (status === 409) {
          form.setError('title', { type: 'server', message: detail });
          return;
        }
        notificationService.showError(detail);
      }
    },
    [requestClose, project.platformProjectId, updateProjectMutation, onSuccess, form, t],
  );

  const submitForm = form.handleSubmit(onSubmit);

  return {
    ...form,
    handleClose,
    submitForm,
    showUnsavedModal,
    handleConfirmLeave,
    handleCancelLeave,
    isDirty: form.formState.isDirty,
    isSubmitting: form.formState.isSubmitting,
  };
}
